import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );

    if (newProductPrice) {
      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      if (newPrice && priceContainer) {
        priceContainer.replaceWith(newPrice);
      }

      const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
      if (!newNote) {
        volumePricingNote?.remove();
      } else if (!volumePricingNote) {
        newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      } else {
        volumePricingNote.replaceWith(newNote);
      }
    }

    // Always overlay the variant's JSON price on top of whatever HTML we have —
    // guards against silent bails above when block ids or markup drift.
    this.#applyVariantPriceFallback(event.detail.resource);
  };

  /**
   * Rewrites the price/compare-at text directly from the variant JSON.
   * @param {{ id?: number|string, price?: number, compare_at_price?: number|null } | null} variant
   */
  #applyVariantPriceFallback(variant) {
    if (!variant || typeof variant.price !== 'number') return;

    const container = this.refs.priceContainer ?? this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    const priceEl = container.querySelector('.price');
    if (priceEl) priceEl.textContent = formatMoney(variant.price, format, currency);

    const compareAtEl = container.querySelector('.compare-at-price');
    const compareAtGroup = compareAtEl?.closest('[role="group"]') ?? compareAtEl;
    const hasCompareAt = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;

    if (compareAtGroup instanceof HTMLElement) {
      if (hasCompareAt && compareAtEl) {
        compareAtEl.textContent = formatMoney(variant.compare_at_price, format, currency);
        compareAtGroup.style.display = '';
      } else {
        compareAtGroup.style.display = 'none';
      }
    }

    if (variant.id != null) this.dataset.variantId = String(variant.id);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
